import React, { useCallback, useEffect, useRef, useState } from 'react';
import MuiTextField from '@material-ui/core/TextField';
import { CircularProgress, InputAdornment } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import { FieldValidation } from '@rjsf/utils';
import {
  CustomFieldExtensionSchema,
  FieldExtensionComponentProps,
} from '@backstage/plugin-scaffolder-react';
import { useApi, discoveryApiRef, identityApiRef } from '@backstage/frontend-plugin-api';

const ACCOUNT_NAME_REGEX = /^[a-z][0-9a-z-]{2,}[0-9a-z]$/;
const ACCOUNT_NAME_REGEX_MESSAGE =
  'Must be at least 4 characters, lowercase alphanumeric with hyphens, '
  + 'starting with a letter and ending with a letter or number';
const DEBOUNCE_MS = 500;

type CheckStatus = 'idle' | 'checking' | 'available' | 'taken' | 'invalid';

// Module-level map shared between the component and the validation function.
// The component writes the latest check result; the validation function reads
// it synchronously when RJSF validates on "Next".
const availabilityCache = new Map<string, { available: boolean; reason?: string }>();

export function AwsAccountNameInput({
  onChange,
  rawErrors,
  required,
  formData,
  formContext,
  schema: { title, description },
}: FieldExtensionComponentProps<string>): JSX.Element {
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);

  const requestId = (formContext as any)?.formData?.requestId as string | undefined;

  const [status, setStatus] = useState<CheckStatus>('idle');
  const [message, setMessage] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const checkName = useCallback(
    async (name: string) => {
      if (!name || !ACCOUNT_NAME_REGEX.test(name)) {
        setStatus(name ? 'invalid' : 'idle');
        setMessage(name ? ACCOUNT_NAME_REGEX_MESSAGE : '');
        availabilityCache.delete(name);
        return;
      }

      setStatus('checking');
      setMessage('Checking availability...');

      try {
        const baseUrl = await discoveryApi.getBaseUrl('infra-requests');
        const { token } = await identityApi.getCredentials();
        const qs = new URLSearchParams({ kind: 'aws-account', name });
        if (requestId) qs.set('excludeRequestId', requestId);
        const resp = await fetch(
          `${baseUrl}/check-name?${qs.toString()}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (resp.ok) {
          const data = await resp.json();
          availabilityCache.set(name, data);
          if (data.available) {
            setStatus('available');
            setMessage('Name is available');
          } else {
            setStatus('taken');
            setMessage(data.reason || 'Name is already taken');
          }
        } else {
          availabilityCache.delete(name);
          setStatus('idle');
          setMessage('');
        }
      } catch {
        availabilityCache.delete(name);
        setStatus('idle');
        setMessage('');
      }
    },
    [discoveryApi, identityApi, requestId],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      onChange(value);

      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => checkName(value), DEBOUNCE_MS);
    },
    [onChange, checkName],
  );

  // Check on mount if editing an existing request (formData is prefilled)
  useEffect(() => {
    if (formData && ACCOUNT_NAME_REGEX.test(formData)) {
      checkName(formData);
    }
  }, []);

  const isError = status === 'taken' || status === 'invalid' || !!rawErrors?.length;
  const helperText = message || description || 'Lowercase alphanumeric with hyphens (e.g. my-new-account)';

  let endAdornment: React.ReactNode = null;
  if (status === 'checking') {
    endAdornment = <InputAdornment position="end"><CircularProgress size={20} /></InputAdornment>;
  } else if (status === 'available') {
    endAdornment = <InputAdornment position="end"><CheckCircleIcon style={{ color: '#4caf50' }} /></InputAdornment>;
  } else if (status === 'taken' || status === 'invalid') {
    endAdornment = <InputAdornment position="end"><ErrorIcon color="error" /></InputAdornment>;
  }

  return (
    <MuiTextField
      fullWidth
      value={formData ?? ''}
      onChange={handleChange}
      label={title ?? 'AWS Account Name'}
      helperText={helperText}
      margin="dense"
      variant="outlined"
      required={required}
      error={isError}
      FormHelperTextProps={status === 'available' ? { style: { color: '#4caf50' } } : undefined}
      InputProps={{ endAdornment }}
    />
  );
}

export const AwsAccountNameInputSchema: CustomFieldExtensionSchema = {
  returnValue: {
    type: 'string',
    title: 'AWS account name',
  },
};

export const AwsAccountNameInputValidation = (
  formData: string,
  validation: FieldValidation,
): void => {
  if (!formData || formData.trim() === '') {
    validation.addError('Account name is required.');
    return;
  }
  if (!ACCOUNT_NAME_REGEX.test(formData)) {
    validation.addError(ACCOUNT_NAME_REGEX_MESSAGE);
    return;
  }

  // Check the cached availability result from the component's async check
  const cached = availabilityCache.get(formData);
  if (cached && !cached.available) {
    validation.addError(cached.reason || 'This name is already taken.');
  }
};
